import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bot, Link2, MousePointerClick, Twitter, Play, Loader2, CheckCircle,
  Clock, TrendingUp, Brain, Target, Sparkles, XCircle, ArrowRight,
  BarChart3, RefreshCw, Eye, ThumbsUp, ThumbsDown, AlertTriangle,
  HelpCircle, Repeat, Server, Zap
} from "lucide-react";
import { ModuleDescription } from "@/components/ModuleDescription";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const Tooltip = ({ text, children }) => (
  <div className="relative group inline-flex items-center">
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-[10px] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-normal max-w-[250px] z-50 leading-relaxed shadow-lg">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
    </div>
  </div>
);

const ActionIcon = ({ action }) => {
  if (action === "internal_linking") return <Link2 className="w-4 h-4 text-indigo-500" />;
  if (action === "ctr_optimizer") return <MousePointerClick className="w-4 h-4 text-orange-500" />;
  if (action === "twitter_intelligence") return <Twitter className="w-4 h-4 text-sky-500" />;
  if (action === "retry_failed_posts") return <Repeat className="w-4 h-4 text-green-500" />;
  return <Bot className="w-4 h-4 text-gray-500" />;
};

const ActionLabel = ({ action }) => {
  const labels = {
    internal_linking: "Linkowanie wewnetrzne",
    ctr_optimizer: "Optymalizacja CTR",
    twitter_intelligence: "Scouting tematow",
    retry_failed_posts: "Ponowienie publikacji",
  };
  return <span>{labels[action] || action}</span>;
};

export default function SeoAgent() {
  const [status, setStatus] = useState(null);
  const [activities, setActivities] = useState([]);
  const [topicQueue, setTopicQueue] = useState([]);
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [activeTab, setActiveTab] = useState("activity");

  const fetchData = useCallback(async () => {
    try {
      const [s, a, t, e] = await Promise.all([
        axios.get(`${API}/seo-agent/status`),
        axios.get(`${API}/seo-agent/activity?limit=30`),
        axios.get(`${API}/seo-agent/topic-queue`),
        axios.get(`${API}/publish/errors`),
      ]);
      setStatus(s.data);
      setActivities(a.data.activities || []);
      setTopicQueue(t.data.topics || []);
      setErrors(e.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/seo-agent/run-now`);
      setTimeout(fetchData, 5000);
    } catch {}
    setRunning(false);
  };

  const retryAll = async () => {
    setRetrying(true);
    try {
      await axios.post(`${API}/publish/retry-all`);
      setTimeout(fetchData, 3000);
    } catch {}
    setRetrying(false);
  };

  const approveTopic = async (title) => {
    try { await axios.post(`${API}/seo-agent/topic-queue/${encodeURIComponent(title)}/approve`); fetchData(); } catch {}
  };
  const rejectTopic = async (title) => {
    try { await axios.post(`${API}/seo-agent/topic-queue/${encodeURIComponent(title)}/reject`); fetchData(); } catch {}
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const s = status?.stats || {};
  const lr = status?.last_runs || {};

  return (
    <div className="space-y-6" data-testid="seo-agent-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Brain className="w-8 h-8 text-emerald-500" /> SEO Agent
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Automatyczne linkowanie, optymalizacja CTR, scouting tematow, retry publikacji
          </p>
          {/* Status agenta */}
          <div className="flex items-center gap-3 mt-2">
            {status?.agent_active ? (
              <div className="flex items-center gap-1.5 px-3 py-1 bg-green-100 rounded-full">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs font-medium text-green-700">Agent aktywny — dziala automatycznie w tle</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-100 rounded-full">
                <span className="w-2 h-2 bg-amber-500 rounded-full" />
                <span className="text-xs font-medium text-amber-700">Agent nieaktywny — uruchom Auto Publisher aby SEO Agent dzialal</span>
              </div>
            )}
            {status?.agent_last_seen && status.agent_last_seen !== "nigdy" && (
              <span className="text-[10px] text-muted-foreground">Ostatnio widziany: {status.agent_last_seen.substring(0,16).replace("T"," ")}</span>
            )}
          </div>
        </div>
      <ModuleDescription title="Jak dziala SEO Agent? (dziala automatycznie w tle razem z Auto Publisherem)">
        <p><strong>SEO Agent</strong> automatyzuje 3 kluczowe zadania SEO w tle:</p>
        <p><strong>1. Linkowanie wewnetrzne (co 24h):</strong> Skanuje artykuly i wstawia 2-4 linki do powiazanych tresci na tej samej stronie. Priorytetyzuje strony z ruchem z Google.</p>
        <p><strong>2. Optymalizacja CTR (co 48h):</strong> Pobiera dane z Google Search Console, znajduje artykuly z duzymi wyswietleniami ale niskim CTR, i przepisuje tytuly uzywajac 2 AI: Grok proponuje, GPT-4o dopracowuje.</p>
        <p><strong>3. Scouting tematow (co 12h):</strong> Monitoruje Google Trends i X/Twitter (przez Grok), proponuje gorące tematy z potencjalem na ruch. Gemini weryfikuje fakty. Tematy z score 8+ sa auto-zatwierdzane.</p>
        <p><strong>"Uruchom teraz"</strong> = odpala caly cykl SEO natychmiast | <strong>"Ponow"</strong> = ponawia nieudane publikacje po sprawdzeniu czy strona dziala.</p>
        <p><strong>Kolejka tematow:</strong> Tematy znalezione przez AI. Kciuk w gore = zatwierdz do publikacji, kciuk w dol = odrzuc.</p>
      </ModuleDescription>

        <div className="flex gap-2">
          {errors && errors.total_errors > 0 && (
            <Tooltip text="Ponow nieudane publikacje. Agent sprawdza czy strona dziala i probuje opublikowac ponownie artykuly ktore wczesniej sie nie udaly.">
              <Button onClick={retryAll} disabled={retrying} variant="outline" className="gap-1.5 border-amber-300" data-testid="retry-all-btn">
                {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Repeat className="w-4 h-4 text-amber-600" />}
                Ponow ({errors.total_errors})
              </Button>
            </Tooltip>
          )}
          <Tooltip text="Uruchom caly SEO Agent natychmiast: sprawdzi linki wewnetrzne, zoptymalizuje tytuly z niskim CTR, znajdzie gorące tematy z Google Trends i X/Twitter. Normalnie dziala automatycznie w tle.">
            <Button onClick={runNow} disabled={running} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="run-seo-now">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {running ? "Pracuje..." : "Uruchom teraz"}
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Stats with tooltips */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Tooltip text="Ile artykulow ma automatycznie wstawione linki wewnetrzne do innych artykulow na tej samej stronie. Wiecej linkow = lepsza struktura SEO.">
          <Card className="w-full"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-indigo-600">{s.posts_with_internal_links || 0}</p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1"><Link2 className="w-3 h-3" /> Posty z linkami</p>
          </CardContent></Card>
        </Tooltip>
        <Tooltip text="Ile tytulow artykulow zostalo automatycznie przepisanych na bardziej klikalne. Agent analizuje CTR z Google Search Console i uzywa AI (Grok + GPT-4o) do optymalizacji.">
          <Card className="w-full"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{s.titles_optimized || 0}</p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1"><MousePointerClick className="w-3 h-3" /> Tytuly zmienione</p>
          </CardContent></Card>
        </Tooltip>
        <Tooltip text="Tematy znalezione przez AI ze scoutingu Google Trends i X/Twitter. Zweryfikowane przez Gemini (fact-checking). Mozesz je zatwierdzic lub odrzucic w zakladce Kolejka tematow.">
          <Card className="w-full"><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-sky-600">{s.topics_in_queue || 0}</p>
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1"><TrendingUp className="w-3 h-3" /> Tematy w kolejce</p>
          </CardContent></Card>
        </Tooltip>
        <Tooltip text="Linkowanie wewnetrzne co 24h. Agent automatycznie wstawia linki miedzy artykulami na tej samej stronie.">
          <Card className="w-full"><CardContent className="p-3 text-center">
            <div className="flex items-center justify-center gap-1"><Link2 className="w-3 h-3 text-indigo-500" /><span className="text-[10px]">Linking (24h)</span></div>
            <p className="text-xs font-medium mt-1">{lr.seo_internal_link === "nigdy" ? "nigdy" : lr.seo_internal_link?.substring(0,10)}</p>
            <p className="text-[9px] text-blue-500 mt-0.5">{status?.next_runs?.seo_internal_link || ""}</p>
          </CardContent></Card>
        </Tooltip>
        <Tooltip text="Optymalizacja tytulow co 48h. Grok proponuje tytuly, GPT-4o dopracowuje SEO. Automatycznie aktualizuje WordPress.">
          <Card className="w-full"><CardContent className="p-3 text-center">
            <div className="flex items-center justify-center gap-1"><MousePointerClick className="w-3 h-3 text-orange-500" /><span className="text-[10px]">CTR (48h)</span></div>
            <p className="text-xs font-medium mt-1">{lr.seo_ctr_optimizer === "nigdy" ? "nigdy" : lr.seo_ctr_optimizer?.substring(0,10)}</p>
            <p className="text-[9px] text-orange-500 mt-0.5">{status?.next_runs?.seo_ctr_optimizer || ""}</p>
          </CardContent></Card>
        </Tooltip>
        <Tooltip text="Scouting tematow co 12h. Google Trends + Grok (X/Twitter) + Gemini fact-check. Auto-approve score 8+.">
          <Card className="w-full"><CardContent className="p-3 text-center">
            <div className="flex items-center justify-center gap-1"><Twitter className="w-3 h-3 text-sky-500" /><span className="text-[10px]">Intel (12h)</span></div>
            <p className="text-xs font-medium mt-1">{lr.seo_twitter_intel === "nigdy" ? "nigdy" : lr.seo_twitter_intel?.substring(0,10)}</p>
            <p className="text-[9px] text-sky-500 mt-0.5">{status?.next_runs?.seo_twitter_intel || ""}</p>
          </CardContent></Card>
        </Tooltip>
      </div>

      {/* Error banner */}
      {errors && errors.total_errors > 0 && (
        <Card className="border-amber-300 bg-amber-50/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="font-medium text-amber-800">{errors.total_errors} nieudanych publikacji</span>
                <Tooltip text="To sa artykuly ktore agent probowal opublikowac ale strona nie odpowiadala (np. przerwa techniczna). Kliknij Ponow aby sprobowac ponownie.">
                  <HelpCircle className="w-3.5 h-3.5 text-amber-500 cursor-help" />
                </Tooltip>
              </div>
              <Button size="sm" variant="outline" onClick={retryAll} disabled={retrying} className="gap-1 text-xs border-amber-400">
                {retrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Repeat className="w-3 h-3" />}
                Ponow wszystkie
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {errors.sites?.slice(0, 6).map((site, i) => (
                <Badge key={i} variant="outline" className="text-[10px] border-amber-300">
                  {site.site_name}: {site.error_count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: "activity", label: "Aktywnosc agenta", icon: Clock, tooltip: "Historia wszystkich dzialan SEO Agenta: linkowanie, optymalizacja CTR, scouting, retry." },
          { id: "topics", label: "Kolejka tematow", icon: TrendingUp, count: topicQueue.filter(t => t.status === "pending").length, tooltip: "Tematy znalezione przez AI ze scoutingu. Zatwierdz (kciuk w gore) lub odrzuc (kciuk w dol). Tematy z score >= 8 sa automatycznie zatwierdzane." },
        ].map(tab => (
          <Tooltip key={tab.id} text={tab.tooltip}>
            <button
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors ${activeTab === tab.id ? "bg-emerald-100 text-emerald-800 font-medium" : "text-muted-foreground hover:bg-muted/50"}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
              {tab.count > 0 && <Badge className="text-[8px] bg-sky-100 text-sky-700 ml-0.5">{tab.count}</Badge>}
            </button>
          </Tooltip>
        ))}
      </div>

      {/* Activity Log */}
      {activeTab === "activity" && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-1.5">
                Log aktywnosci
                <Tooltip text="Kazdy wpis to jedno uruchomienie modulu SEO Agenta. Pokazuje co zrobil, ile linkow dodal, jakie tytuly zmienil, jakie tematy znalazl.">
                  <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                </Tooltip>
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={fetchData} className="h-7"><RefreshCw className="w-3 h-3" /></Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {activities.length === 0 && (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  <Bot className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Agent jeszcze nie dzialal. Kliknij "Uruchom teraz" aby wystartowac.</p>
                  <p className="text-[10px] mt-1">Agent automatycznie uruchamia sie co 5 minut w tle w godzinach pracy.</p>
                </div>
              )}
              {activities.map((a, i) => (
                <div key={i} className="px-4 py-3 border-b hover:bg-muted/20" data-testid={`activity-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <ActionIcon action={a.action} />
                    <span className="text-sm font-medium"><ActionLabel action={a.action} /></span>
                    <Badge className={`text-[8px] ${a.status === "ok" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{a.status}</Badge>
                    <span className="text-[10px] text-muted-foreground ml-auto">{a.created_at?.substring(0, 16).replace("T"," ")}</span>
                  </div>
                  
                  {a.action === "internal_linking" && a.details && (
                    <div className="text-xs text-muted-foreground ml-6">
                      <span className="text-indigo-600 font-medium">{a.details.total_links_added || 0} linkow</span> w {a.details.sites_processed || 0} stronach
                      {a.details.details?.slice(0,3).map((d,j) => (
                        <span key={j} className="ml-2">{d.site}: {d.links_added}</span>
                      ))}
                    </div>
                  )}
                  
                  {a.action === "ctr_optimizer" && a.details && (
                    <div className="ml-6 space-y-1">
                      <span className="text-xs text-orange-600 font-medium">{a.details.optimized || 0} tytulow</span>
                      {a.details.details?.slice(0,3).map((d,j) => (
                        <div key={j} className="text-xs">
                          <span className="text-muted-foreground line-through">{d.old_title?.substring(0,40)}</span>
                          <ArrowRight className="w-3 h-3 inline mx-1" />
                          <span className="text-orange-600 font-medium">{d.new_title?.substring(0,40)}</span>
                          <span className="text-[10px] text-muted-foreground ml-1">(CTR {d.ctr}%, {d.impressions} imp.)</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {a.action === "twitter_intelligence" && a.details && (
                    <div className="ml-6 space-y-1">
                      <span className="text-xs text-muted-foreground">
                        {a.details.trends_analyzed || 0} trendow przeanalizowanych, {a.details.verified || 0} zweryfikowanych, {a.details.added_to_queue || 0} w kolejce
                      </span>
                      {a.details.topics?.slice(0,3).map((t,j) => (
                        <div key={j} className="text-xs flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-sky-500" />
                          <span className="font-medium">{t.title?.substring(0,50)}</span>
                          <Badge className="text-[7px] bg-emerald-100 text-emerald-700">{t.score}/10</Badge>
                          <span className="text-muted-foreground">{t.category}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {a.action === "retry_failed_posts" && a.details && (
                    <div className="ml-6 text-xs">
                      <span className="text-green-600 font-medium">{a.details.recovered || 0} przywroconych</span>
                      <span className="text-muted-foreground ml-2">z {a.details.retried || 0} probowanych</span>
                      {a.details.still_down > 0 && <span className="text-red-500 ml-2">{a.details.still_down} stron niedostepnych</span>}
                    </div>
                  )}
                </div>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Topic Queue */}
      {activeTab === "topics" && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4" /> Kolejka tematow z AI
              <Tooltip text="Tematy znalezione przez AI z Google Trends + X/Twitter. Grok proponuje, Gemini weryfikuje fakty. Tematy z score >= 8 i zweryfikowane sa auto-zatwierdzane i trafiaja do Auto Publishera.">
                <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </Tooltip>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {topicQueue.length === 0 && (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Brak tematow w kolejce.</p>
                  <p className="text-[10px] mt-1">Agent co 12h skanuje Google Trends i analizuje X/Twitter. Tematy pojawia sie automatycznie.</p>
                </div>
              )}
              {topicQueue.map((t, i) => (
                <div key={i} className="px-4 py-3 border-b hover:bg-muted/20" data-testid={`topic-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-medium">{t.title}</span>
                        <Badge className={`text-[8px] ${t.status === "pending" ? "bg-amber-100 text-amber-700" : t.status === "approved" || t.status === "auto_approved" ? "bg-green-100 text-green-700" : t.status === "queued_for_publish" ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>
                          {t.status === "auto_approved" ? "auto" : t.status === "queued_for_publish" ? "w kolejce pub." : t.status}
                        </Badge>
                        <Tooltip text={`Ocena potencjalu tematu przez AI (1-10). Wyzszy = wieksze szanse na ruch. Score >= 8 = auto-zatwierdzenie.`}>
                          <Badge className="text-[8px] bg-emerald-100 text-emerald-700 cursor-help">{t.score}/10</Badge>
                        </Tooltip>
                        {t.verified && (
                          <Tooltip text="Temat zweryfikowany przez Gemini (fact-checking). Informacje sa prawdziwe.">
                            <CheckCircle className="w-3 h-3 text-green-500 cursor-help" />
                          </Tooltip>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                        <Badge variant="outline" className="text-[8px]">{t.category}</Badge>
                        {t.keywords?.length > 0 && <span>KW: {t.keywords.slice(0,3).join(", ")}</span>}
                        {t.target_site_name && <span className="text-blue-600">Strona: {t.target_site_name}</span>}
                        <span className="ml-auto">{t.created_at?.substring(0,10)}</span>
                      </div>
                      {t.reason && <p className="text-[10px] text-muted-foreground mt-0.5">{t.reason.substring(0,120)}</p>}
                    </div>
                    {t.status === "pending" && (
                      <div className="flex gap-1 shrink-0">
                        <Tooltip text="Zatwierdz temat do publikacji. Agent Auto Publisher wygeneruje artykul i opublikuje go na odpowiedniej stronie.">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => approveTopic(t.title)}>
                            <ThumbsUp className="w-3.5 h-3.5 text-green-600" />
                          </Button>
                        </Tooltip>
                        <Tooltip text="Odrzuc temat. Agent nie bedzie go publikowal.">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => rejectTopic(t.title)}>
                            <ThumbsDown className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
